export interface GalleryConnectionInfo {
  saveData?: boolean;
  effectiveType?: string;
  downlink?: number;
}

// The gallery's <img sizes>. The PDP LCP preload must use the same string or
// the browser can pick a different rendition (it differed at exactly 1024px).
export const GALLERY_SIZES = "(max-width: 1023px) 100vw, 50vw";

export const GALLERY_WARM_CONCURRENCY = 2;
export const CONSTRAINED_WARM_AHEAD = 1;
export const NORMAL_WARM_AHEAD = 2;

/**
 * Keep a small rolling window after the product hero. The remaining photos are
 * fetched only as the visitor advances, so a 12-photo gallery does not become
 * a 12-photo page-load cost on mobile.
 */
export function galleryWarmLimit(
  itemCount: number,
  connection?: GalleryConnectionInfo | null
) {
  const secondaryCount = Math.max(0, Math.floor(itemCount) - 1);
  if (secondaryCount === 0) return 0;

  // Data Saver means no speculative transfers; navigation still loads normally.
  if (connection?.saveData) return 0;

  const constrained =
    connection?.downlink && connection.downlink < 1.5
      ? true
      : ["slow-2g", "2g", "3g"].includes(
          String(connection?.effectiveType || "").toLowerCase()
        );

  if (constrained) {
    return Math.min(secondaryCount, CONSTRAINED_WARM_AHEAD);
  }

  return Math.min(secondaryCount, NORMAL_WARM_AHEAD);
}

/** Follow swipe direction, then fill from the other side at the gallery edge. */
export function buildGalleryWarmOrder(
  itemCount: number,
  activeIndex: number,
  warmLimit = Number.POSITIVE_INFINITY,
  direction = 1
) {
  const count = Math.max(0, Math.floor(itemCount));
  if (count <= 1) return [];

  const centre = Math.max(0, Math.min(Math.floor(activeIndex), count - 1));
  const order: number[] = [];

  const step = direction < 0 ? -1 : 1;
  for (let index = centre + step; index >= 0 && index < count; index += step)
    order.push(index);
  for (let index = centre - step; index >= 0 && index < count; index -= step)
    order.push(index);

  return order.slice(0, Math.max(0, Math.floor(warmLimit)));
}
